/**
 * Sticky CTA bar for treatment pages.
 *
 * The treatments counterpart of `sticky-add-to-cart`, minus everything tied to a
 * product: no variants, no quantity, no cart. It mirrors the same show/hide
 * behaviour — reveal once the page CTA has scrolled above the viewport, hide
 * again when it comes back or when the footer approaches.
 *
 * Its contents are read from the hero at runtime rather than duplicated in the
 * CMS, so the title, prices and CTA have a single source of truth:
 *   - title  → `.product__title`
 *   - prices → `.treatment__prices` (copied as markup, dots and all)
 *   - CTA    → `.treatment-cta-button` (label and href)
 */
class TreatmentStickyCta extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #ctaObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByFooter = false;

  connectedCallback() {
    this.bar = this.querySelector('.sticky-add-to-cart__bar');
    this.titleTarget = this.querySelector('[data-sticky-title]');
    this.pricesTarget = this.querySelector('[data-sticky-prices]');
    this.ctaTarget = this.querySelector('[data-sticky-cta]');

    this.ctaSource = document.querySelector('.treatment-cta-button');

    // Without a CTA to mirror there is nothing to stick.
    if (!this.bar || !this.ctaTarget || !this.ctaSource) {
      this.hidden = true;
      return;
    }

    this.#fill();
    this.#observe();
  }

  disconnectedCallback() {
    this.#ctaObserver?.disconnect();
    this.#footerObserver?.disconnect();
  }

  /** Copies the hero's title, prices and CTA into the bar. */
  #fill() {
    const titleSource = document.querySelector('.product__title');
    if (this.titleTarget && titleSource) {
      this.titleTarget.textContent = titleSource.textContent?.trim() ?? '';
    }

    const pricesSource = document.querySelector('.treatment__prices');
    if (this.pricesTarget && pricesSource) {
      // Markup, not text — the prices carry their own spans and dot separators.
      this.pricesTarget.innerHTML = pricesSource.innerHTML;
    }

    this.ctaTarget.textContent = this.ctaSource.textContent?.trim() ?? '';
    this.ctaTarget.setAttribute('href', this.ctaSource.getAttribute('href') ?? '#');

    // Carry the link's behaviour too, so the two stay in step if the booking
    // URL ever opens in a new tab.
    for (const attribute of ['target', 'rel']) {
      const value = this.ctaSource.getAttribute(attribute);
      if (value) this.ctaTarget.setAttribute(attribute, value);
      else this.ctaTarget.removeAttribute(attribute);
    }
  }

  #observe() {
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    this.#ctaObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        // Only stick once the CTA is above the viewport, not before it is reached.
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) this.#show();
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByFooter = false;
        this.#hide();
      }
    });

    this.#ctaObserver.observe(this.ctaSource);

    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByFooter = true;
          this.#hide();
        } else if (!entry.isIntersecting && this.#hiddenByFooter) {
          const rect = this.ctaSource.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByFooter = false;
            this.#show();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#footerObserver.observe(footer);
  }

  #show() {
    this.#isStuck = true;
    this.bar.dataset.stuck = 'true';
  }

  #hide() {
    this.#isStuck = false;
    this.bar.dataset.stuck = 'false';
  }
}

if (!customElements.get('treatment-sticky-cta')) {
  customElements.define('treatment-sticky-cta', TreatmentStickyCta);
}
